import { DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { LocationService } from '@app/core/locations/services/location.service';
import { DefectiveReportService } from '@app/core/purchase-orders/services/defective-report.service';
import { RfqLotService } from '@app/core/rfq-lots/services/rfq-lots.service';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { environment } from '@app/env-service';
import { EFeatureFlag } from '@app/feature-toggle-list';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';

import { SnackBarConfig } from '../../../../v2/modules';
import { DefectiveReportsFormRfqsComponent } from '../defective-reports-form-rfqs/defective-reports-form-rfqs.component';

import { Lot, Location } from './defective-reports-form-config';

@Component({
  selector: 'cnc-defective-reports-form',
  templateUrl: './defective-reports-form.component.html',
  styleUrls: ['./defective-reports-form.component.scss'],
})
export class DefectiveReportsFormComponent implements OnInit, OnDestroy {
  url :string = environment.url;
  datePipe = new DatePipe('en');

  loading :boolean = false;
  pageTitle :string = 'Defective Report Form';

  formData :UntypedFormGroup;
  totalUnits :number = 0;
  totalCost :number = 0;
  locations :any[] = [];
  unitOfMeasures :any[] = [];
  rfqLot :Lot[] = [];
  locationWithoutParent :Location[] = [];
  ftLocationBins :boolean = false;
  ftLotTracking :boolean = false;
  private onDestroy$ = new Subject();

  settingsSubscription :Subscription;

  @Input() defectiveReport;

  constructor(
    public activeModal :NgbActiveModal,
    private modalSvc :NgbModal,
    private fb :UntypedFormBuilder,
    private toastr :ToastrService,
    public drSvc :DefectiveReportService,
    private cd :ChangeDetectorRef,
    private translate :TranslateService,
    public locationSvc :LocationService,
    private rfqLotSvc :RfqLotService,
    private settingsSvc :SettingsService,
    private ft :FeatureToggleService,
    private snackBar :MatSnackBar,
  ) {
    this.ftLotTracking = this.ft.featureEnabled(EFeatureFlag.LOT_TRACKING);
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
    if (this.settingsSubscription) {
      this.settingsSubscription.unsubscribe();
    }
  }

  ngOnInit() :void {
    this.initTrackInventoryByLocation();
    this.getLocations();
    this.loadLocationsWithoutParent().subscribe();
    this.formData = this.fb.group({
      reported_date: [
        this.datePipe.transform(new Date(), 'yyyy-MM-dd'),
        Validators.required,
      ],
      reported_by: [null, Validators.required],
      defect_type: [null, Validators.required],
      // reported_at: [null, Validators.required],
      total_quantity: [null],
      total_tax: [null],
      total_cost: [null],
      items: this.fb.array([]),
      isAffectingInventory: [true],
    });

    if (this.defectiveReport) {
      this.formData.patchValue(this.defectiveReport);
      this.formData
        .get('reported_date')
        .patchValue(
          this.datePipe.transform(this.defectiveReport.reported_date, 'yyyy-MM-dd'),
        );
      // load items
      this.defectiveReport.defective_report_rfq.forEach((drRfq :any) => {
        if (drRfq.purchase_order_rfq && drRfq.purchase_order_rfq?.rfq) {
          this.unitOfMeasures[drRfq.purchase_order_rfq.rfq.id] =
            drRfq.purchase_order_rfq.rfq.unit_of_measures;
        }

        if (drRfq.rfq) {
          this.unitOfMeasures[drRfq.rfq.id] = drRfq.rfq.unit_of_measures;
          if (drRfq.rfq.unit_of_measures.length <= 0) {
            this.unitOfMeasures[drRfq.rfq.id] = drRfq.rfq.parent.unit_of_measures;
          }
        }

        const rfqId = drRfq?.purchase_order_rfq?.rfq_id
          ? drRfq.purchase_order_rfq.rfq_id
          : drRfq?.rfq?.id
            ? drRfq?.rfq?.id
            : 0;

        this.addItem({
          purchase_order_id: drRfq.purchase_order_id,
          purchase_order_rfq_id: drRfq.purchase_order_rfq_id,
          quantity: drRfq.quantity,
          sku: drRfq.sku,
          product_name: drRfq.product_name,
          unit: drRfq.unit,
          unit_price: drRfq.unit_price,
          line_total: drRfq.line_total,
          factory_name: drRfq.factory_name,
          factory_id: drRfq.factory_id,
          receiving_report_id: drRfq.receiving_report_id,
          received_date: drRfq.received_date,
          remarks: drRfq.remarks,
          warehouse_id: drRfq.stock ? drRfq.stock.warehouse_id : 0,
          location_id: drRfq.location_id ?? 0,
          lot_id: drRfq.lot_id ?? 0,
          lot_tracking: drRfq?.rfq?.lot_tracking ?? 0,
          stock_id: drRfq.stock ? drRfq.stock.id : 0,
          id: drRfq.id ? drRfq.id : 0,
          rfq_id: rfqId,
          defective_uom_id: drRfq.defective_uom_id,
        });
      });
      this.totalCost = this.defectiveReport.total_cost;
      this.totalUnits = this.defectiveReport.total_quantity;
    }
  }

  getLocations() :void {
    this.locationSvc.getAll({ all: 1, parent_id: 0 }).subscribe((resp) => {
      this.locations = resp;
    });
  }
  getLocation(locationId :number) :any {
    const location = this.locations.find((row) => row.id == locationId);
    if (location) {
      return location.location_name;
    }

    return '';
  }

  private loadLocationsWithoutParent() :Observable<any> {
    return this.locationSvc.getAll({ all: 1 }).pipe(
      tap((response :any) => {
        this.locationWithoutParent = response;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  getLocationWithoutParent(locationId :number) :string {
    const location = this.locationWithoutParent.find(
      (row) => row['id'] === locationId,
    );
    if (location) {
      return location['location_name'];
    }

    return '';
  }

  private loadRfqLot(rfqIds :number[]) :Observable<any> {
    return this.rfqLotSvc
      .getByRfqIds({
        rfqIds: rfqIds.join(','),
        items: 1000,
      })
      .pipe(
        tap((response :any) => {
          if (response) {
            this.rfqLot = this.rfqLot.concat(response.data);
          }
        }),
        takeUntil(this.onDestroy$),
      );
  }

  getRfqLotNumber(rfqId :number, lotId :number) :string {
    const lot = this.rfqLot.find(
      (row) => row['id'] === lotId && row['rfq_id'] === rfqId,
    );
    if (lot) {
      return lot['lot_number'];
    }

    return '';
  }

  enabledBinLocationAndLotTracking(item :any) :boolean {
    return (
      item?.value?.lot_tracking === 1 && this.ftLocationBins && this.ftLotTracking
    );
  }

  private initTrackInventoryByLocation() :void {
    this.settingsSubscription =
      this.settingsSvc.trackInventoryByLocationValue$.subscribe(
        (enabled :boolean) => {
          this.ftLocationBins = enabled;
        },
      );
  }

  save() :void {
    if (this.formData.invalid) {
      Swal.fire({
        title: this.translate.instant('MESSAGES.FORM_ERROR.TITLE'),
        text: this.translate.instant('MESSAGES.FORM_ERROR.TEXT'),
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });

      return;
    }

    const payload :any = this.formData.value;

    if (payload.items.length == 0) {
      Swal.fire({
        title: 'SKUs/Products are required.',
        text: 'Please add at least one(1) item/rfq reported.',
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });

      return;
    }
    payload.total_cost = this.totalCost;
    payload.total_quantity = this.totalUnits;
    payload.total_tax = 0;

    this.loading = true;

    if (this.defectiveReport) {
      this.snackBar.open(
        'Updating posted defective report is no longer allowed.',
        'Failed',
        {
          duration: SnackBarConfig.DEFAULT.duration,
        },
      );

      return;

      this.drSvc.updateDefectiveReport(this.defectiveReport.id, payload).subscribe(
        (resp) => {
          this.toastr.success('Defective report saved.');
          this.loading = false;
          this.activeModal.close(resp);
        },
        (error) => {
          this.loading = false;
          this.toastr.error(
            'Error saving defective report. Please try again later or contact support.',
          );
        },
      );
    } else {
      this.drSvc.createDefectiveReport(payload).subscribe(
        (resp) => {
          this.toastr.success('Defective report saved.');
          this.loading = false;
          this.activeModal.close(resp);
        },
        (error) => {
          this.loading = false;
          this.toastr.error(
            'Error saving defective report. Please try again later or contact support.',
          );
        },
      );
    }
  }

  computeLineTotal(idx) :void {
    const items :UntypedFormArray = this.formData.get('items') as UntypedFormArray;
    const lineItem :UntypedFormGroup = items.at(idx) as UntypedFormGroup;
    const lineTotal :number = lineItem.value.quantity * lineItem.value.unit_price;

    lineItem.get('line_total').patchValue(lineTotal);
    this.computeTotal();
  }

  computeTotal() :void {
    const items :UntypedFormArray = this.formData.get('items') as UntypedFormArray;
    this.totalUnits = 0;
    this.totalCost = 0;

    items.value.forEach((row) => {
      this.totalUnits += +row.quantity;
      this.totalCost += +row.line_total;
    });
  }

  addItem(item :any) :void {
    const items :UntypedFormArray = this.formData.get('items') as UntypedFormArray;
    const itemGroup :UntypedFormGroup = this.fb.group({
      purchase_order_id: [item.purchase_order_id],
      purchase_order_rfq_id: [item.purchase_order_rfq_id],
      quantity: [item.quantity],
      sku: [item.sku],
      product_name: [item.product_name],
      unit: [item.unit],
      unit_price: [item.unit_price],
      line_total: [item.line_total],
      factory_name: [item.factory_name],
      factory_id: [item.factory_id],
      receiving_report_id: [item.receiving_report_id],
      received_date: [item.received_date],
      remarks: [item.remarks],
      warehouse_id: [item.warehouse_id],
      location_id: [item.location_id],
      lot_id: [item.lot_id],
      lot_tracking: [item.lot_tracking ?? 0],
      rfq_id: [item.rfq_id ? item.rfq_id : 0],
      stock_id: [item.stock_id ? item.stock_id : 0],
      id: [item.id ? item.id : 0],
      defective_uom_id: [item.defective_uom_id ? item.defective_uom_id : 0],
    });

    if (item) {
      if (item.unit_of_measures) {
        this.unitOfMeasures[item.rfq_id] = item.unit_of_measures;
      }
      if (item.parent?.unit_of_measures) {
        this.unitOfMeasures[item.rfq_id] = item.parent.unit_of_measures;
      }
    }

    items.push(itemGroup);
    const rfqIds = items.value.map((item) => item.rfq_id);
    this.loadRfqLot(rfqIds).subscribe();
  }

  addPOItems() :void {
    const modalRef = this.modalSvc.open(DefectiveReportsFormRfqsComponent, {
      size: 'lg',
      windowClass: 'po-rr-modal',
    });
    modalRef.componentInstance.ftLocationBins = this.ftLocationBins;
    modalRef.componentInstance.ftLotTracking = this.ftLotTracking;
    modalRef.result.then(
      (item) => {
        this.addItem(item);
        this.computeTotal();
      },
      (_) => {},
    );
  }
}
